import type { JSONContent } from "@tiptap/react";

export enum TriggerEvent {
  APPOINTMENT_BOOKED = "APPOINTMENT_BOOKED",
  APPOINTMENT_STARTING = "APPOINTMENT_STARTING",
  APPOINTMENT_COMPLETED = "APPOINTMENT_COMPLETED",
  APPOINTMENT_CANCELLED = "APPOINTMENT_CANCELLED",
  APPOINTMENT_NO_SHOW = "APPOINTMENT_NO_SHOW",
  MEMBERSHIP_STARTED = "MEMBERSHIP_STARTED",
  MEMBERSHIP_RENEWING = "MEMBERSHIP_RENEWING",
}

export enum EffectType {
  SEND_SMS = "SEND_SMS",
  SEND_EMAIL = "SEND_EMAIL",
  CHANGE_STATUS = "CHANGE_STATUS",
  CREATE_TASK = "CREATE_TASK",
}

export enum ConditionType {
  FORM_COMPLETED = "FORM_COMPLETED",
  FORM_NOT_COMPLETED = "FORM_NOT_COMPLETED",
  EVENT_CONFIRMED = "EVENT_CONFIRMED",
  EVENT_NOT_CONFIRMED = "EVENT_NOT_CONFIRMED",
  HAS_NO_FUTURE_APPOINTMENTS = "HAS_NO_FUTURE_APPOINTMENTS",
  NOT_CHECKED_IN = "NOT_CHECKED_IN",
  IN_COHORT = "IN_COHORT",
  NOT_IN_COHORT = "NOT_IN_COHORT",
}

// --- Display config ---

export interface TriggerConfigEntry {
  label: string;
  description: string;
  supportsNegativeDelay: boolean;
  defaultDirection: "before" | "after";
  excludedEffects?: Set<EffectType>;
}

export const TRIGGER_CONFIG: Record<TriggerEvent, TriggerConfigEntry> = {
  [TriggerEvent.APPOINTMENT_BOOKED]: {
    label: "Appointment Booked",
    description: "When a new appointment is booked",
    supportsNegativeDelay: false,
    defaultDirection: "after",
  },
  [TriggerEvent.APPOINTMENT_STARTING]: {
    label: "Appointment Starting",
    description: "Before or after the appointment start time",
    supportsNegativeDelay: true,
    defaultDirection: "before",
  },
  [TriggerEvent.APPOINTMENT_COMPLETED]: {
    label: "Appointment Completed",
    description: "After the appointment is marked complete",
    supportsNegativeDelay: false,
    defaultDirection: "after",
  },
  [TriggerEvent.APPOINTMENT_CANCELLED]: {
    label: "Appointment Cancelled",
    description: "When the appointment is cancelled",
    supportsNegativeDelay: false,
    defaultDirection: "after",
    excludedEffects: new Set([EffectType.CHANGE_STATUS]),
  },
  [TriggerEvent.APPOINTMENT_NO_SHOW]: {
    label: "Appointment No-Show",
    description: "When a patient is marked as a no-show",
    supportsNegativeDelay: false,
    defaultDirection: "after",
    excludedEffects: new Set([EffectType.CHANGE_STATUS]),
  },
  [TriggerEvent.MEMBERSHIP_STARTED]: {
    label: "Membership Started",
    description: "Once, when a new enrollment becomes active",
    supportsNegativeDelay: false,
    defaultDirection: "after",
    excludedEffects: new Set([
      EffectType.CHANGE_STATUS,
      EffectType.CREATE_TASK,
    ]),
  },
  [TriggerEvent.MEMBERSHIP_RENEWING]: {
    label: "Before Renewal Charge",
    description: "Before every billing charge, once per billing date",
    supportsNegativeDelay: true,
    defaultDirection: "before",
    excludedEffects: new Set([
      EffectType.CHANGE_STATUS,
      EffectType.CREATE_TASK,
    ]),
  },
};

export const EFFECT_LABELS: Record<EffectType, string> = {
  [EffectType.SEND_SMS]: "Send SMS",
  [EffectType.SEND_EMAIL]: "Send Email",
  [EffectType.CHANGE_STATUS]: "Change Status",
  [EffectType.CREATE_TASK]: "Create Task",
};

export function getAvailableEffects(trigger: TriggerEvent): EffectType[] {
  const excluded = TRIGGER_CONFIG[trigger].excludedEffects;
  if (!excluded) return Object.values(EffectType);
  return Object.values(EffectType).filter((e) => !excluded.has(e));
}

export const CONDITION_LABELS: Record<ConditionType, string> = {
  [ConditionType.FORM_COMPLETED]: "Patient completed form",
  [ConditionType.FORM_NOT_COMPLETED]: "Patient has not completed form",
  [ConditionType.EVENT_CONFIRMED]: "Patient confirmed appointment",
  [ConditionType.EVENT_NOT_CONFIRMED]: "Patient has not confirmed appointment",
  [ConditionType.HAS_NO_FUTURE_APPOINTMENTS]:
    "Patient has no upcoming appointment for selected service(s)",
  [ConditionType.NOT_CHECKED_IN]: "Patient has not checked in",
  [ConditionType.IN_COHORT]: "Patient is in cohort",
  [ConditionType.NOT_IN_COHORT]: "Patient is not in cohort",
};

// --- Condition configs ---

export interface FormConditionConfig {
  form_ids: string[];
}

export interface ServiceConditionConfig {
  service_ids: string[];
}

export interface CohortConditionConfig {
  cohort_id: string;
}

export enum StepExecutionStatus {
  SUCCESS = "SUCCESS",
  SKIPPED = "SKIPPED",
}

// --- Effect configs ---

export interface AttachmentRef {
  gcsPath: string;
  filename: string;
  // Transient preview URL resolved on read (never persisted). Present on
  // freshly-uploaded refs and on refs loaded from the sequence detail API.
  signedUrl?: string;
}

export interface SmsEffectConfig {
  template: string;
  attachments?: AttachmentRef[];
}

export interface EmailEffectConfig {
  template: string;
  subject: string;
  templateJson?: Record<string, unknown>;
  attachments?: AttachmentRef[];
}

export interface StatusEffectConfig {
  target_status: string;
}

export interface TaskEffectConfig {
  title: string;
  description?: string;
  priority: string;
  status?: string;
  team_id?: string;
  assignee_id?: string | null;
  due_date_offset_minutes?: number;
  start_date_offset_minutes?: number;
}

// --- Step schemas ---

export interface SequenceStepCreate {
  id?: string;
  triggerEvent: TriggerEvent;
  delayMinutes: number;
  effectType: EffectType;
  effectConfig:
    | SmsEffectConfig
    | EmailEffectConfig
    | StatusEffectConfig
    | TaskEffectConfig;
  conditionType?: ConditionType | null;
  conditionConfig?:
    | FormConditionConfig
    | ServiceConditionConfig
    | CohortConditionConfig
    | null;
  displayOrder: number;
  isActive: boolean;
}

export interface SequenceStepDetail {
  id: string;
  triggerEvent: TriggerEvent;
  delayMinutes: number;
  effectType: EffectType;
  config: {
    effect?:
      | SmsEffectConfig
      | EmailEffectConfig
      | StatusEffectConfig
      | TaskEffectConfig;
    condition?: {
      form_ids?: string[];
      service_ids?: string[];
      cohort_id?: string;
    };
  } | null;
  conditionType: ConditionType | null;
  displayOrder: number;
  isActive: boolean;
  createdDate: string;
  updatedDate: string | null;
}

// --- Sequence schemas ---

export interface SequenceUpsert {
  name?: string | null;
  isActive: boolean;
  steps: SequenceStepCreate[];
}

export interface SequenceDetail {
  id: string;
  name: string | null;
  isActive: boolean;
  serviceId: string | null;
  steps: SequenceStepDetail[];
  createdDate: string;
  updatedDate: string | null;
}

// --- Step draft (form editing state for service-drawer sequences tab) ---

export interface SmsEffectDraft {
  template: string;
}

export interface EmailEffectDraft {
  template: string;
  subject: string;
  templateJson?: JSONContent;
  attachments: { gcsPath: string; filename: string }[];
}

export interface StatusEffectDraft {
  targetStatus: string;
}

export interface TaskEffectDraft {
  title: string;
  description: string;
  priority: string;
  status: string;
  teamId: string | null;
  assigneeId: string | null;
  dueDateOffsetMinutes: number | null;
  startDateOffsetMinutes: number | null;
}

// Maps each EffectType string value to its draft config type.
export type EffectDraftByType = {
  [EffectType.SEND_SMS]: SmsEffectDraft;
  [EffectType.SEND_EMAIL]: EmailEffectDraft;
  [EffectType.CHANGE_STATUS]: StatusEffectDraft;
  [EffectType.CREATE_TASK]: TaskEffectDraft;
};

// --- Step draft (local editing state) ---

interface StepDraftBase {
  id?: string;
  key: string;
  triggerEvent: TriggerEvent;
  delayMinutes: number;
  conditionType: ConditionType | null;
  conditionFormIds: string[];
  conditionServiceIds: string[];
  conditionCohortId: string | null;
  isActive: boolean;
}

export interface SmsStepDraft extends StepDraftBase {
  effectType: EffectType.SEND_SMS;
  effectConfig: SmsEffectDraft;
}

export interface EmailStepDraft extends StepDraftBase {
  effectType: EffectType.SEND_EMAIL;
  effectConfig: EmailEffectDraft;
}

export interface StatusStepDraft extends StepDraftBase {
  effectType: EffectType.CHANGE_STATUS;
  effectConfig: StatusEffectDraft;
}

export interface TaskStepDraft extends StepDraftBase {
  effectType: EffectType.CREATE_TASK;
  effectConfig: TaskEffectDraft;
}

export type StepDraft =
  | SmsStepDraft
  | EmailStepDraft
  | StatusStepDraft
  | TaskStepDraft;

export interface ServiceSequenceFormValue {
  isActive: boolean;
  steps: StepDraft[];
}
